import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { api, ApiError, type AuditLogEntry } from '../lib/api'

/**
 * The Immutable Audit Log — an append-only, hash-chained record of who changed what. "Verify
 * integrity" is not decoration: it recomputes the whole chain server-side and reports whether
 * it still checks out, which is the actual proof behind "tamper-evident" rather than a claim
 * the screen makes on the store's behalf.
 */
export function AuditLog() {
  const queryClient = useQueryClient()
  const [actionFilter, setActionFilter] = useState('')
  const [retentionInput, setRetentionInput] = useState('')
  const [retentionTouched, setRetentionTouched] = useState(false)

  const settings = useQuery({ queryKey: ['audit-log-settings'], queryFn: api.auditLogSettings, retry: false })
  const entries = useQuery({
    queryKey: ['audit-log', actionFilter],
    queryFn: () => api.auditLog({ action: actionFilter || undefined }),
    enabled: !settings.isError,
    retry: false,
  })

  const verify = useMutation({ mutationFn: api.verifyAuditLog })
  const saveRetention = useMutation({
    mutationFn: (days: number | null) => api.saveAuditLogRetention(days),
    onSuccess: () => {
      setRetentionTouched(false)
      void queryClient.invalidateQueries({ queryKey: ['audit-log-settings'] })
    },
  })

  const needsAddon = settings.error instanceof ApiError && settings.error.status === 402

  if (settings.isPending) return <div className="cad-card" style={{ height: '240px' }} />

  if (needsAddon) {
    return (
      <>
        <header className="stack gap-1">
          <span className="cad-eyebrow">Compliance</span>
          <h1 className="cad-title">Immutable Audit Log</h1>
        </header>
        <section className="cad-card">
          <div className="stack gap-3">
            <p className="cad-muted" style={{ maxWidth: '64ch' }}>
              An append-only, tamper-evident record of every team-side action — who invited
              whom, what a broadcast sent, which integration was connected, what data left the
              workspace — with a configurable retention policy and a "verify integrity" check
              that recomputes the whole chain rather than trusting a claim.
            </p>
            <p className="cad-note">
              This needs the Immutable Audit Log module. Add it from billing to turn it on.
            </p>
          </div>
        </section>
      </>
    )
  }

  if (settings.isError) {
    return (
      <div className="cad-card">
        <p className="cad-muted">Unavailable.</p>
      </div>
    )
  }

  const retentionDays = settings.data!.retentionDays
  const displayedRetention = retentionTouched ? retentionInput : (retentionDays?.toString() ?? '')
  // Blank means "forever" and is valid; anything typed must be a whole number of at least one
  // day — the input's own `min={1}` never enforces this because the button has no form to submit
  // against, so a stray "0" or "-5" would otherwise reach the API and bounce off its own check.
  const retentionValid =
    displayedRetention.trim() === '' ||
    (Number.isInteger(Number(displayedRetention)) && Number(displayedRetention) >= 1)

  return (
    <>
      <header className="stack gap-1">
        <span className="cad-eyebrow">Compliance</span>
        <h1 className="cad-title">Immutable Audit Log</h1>
        <p className="cad-muted" style={{ maxWidth: '64ch' }}>
          Every entry chains to the one before it — editing or deleting a row breaks every hash
          after it, which "Verify integrity" below would catch.
        </p>
      </header>

      <section className="cad-card">
        <div className="stack gap-3">
          <div className="row between wrap gap-2" style={{ alignItems: 'center' }}>
            <h2 className="cad-section-title">Chain integrity</h2>
            <button type="button" className="cad-btn" data-variant="quiet" disabled={verify.isPending} onClick={() => verify.mutate()}>
              {verify.isPending ? 'Verifying…' : 'Verify integrity'}
            </button>
          </div>
          {verify.data && (
            <p className="cad-note" style={verify.data.valid ? undefined : { borderColor: 'var(--cad-bad)' }}>
              {verify.data.valid
                ? `Verified — ${verify.data.totalRows} ${verify.data.totalRows === 1 ? 'entry' : 'entries'}, chain intact.`
                : `Broken at entry ${verify.data.brokenAtSeq}: ${verify.data.reason}`}
            </p>
          )}
          {verify.error && (
            <p className="cad-note" style={{ borderColor: 'var(--cad-bad)' }}>
              {verify.error instanceof Error ? verify.error.message : 'Could not verify the chain.'}
            </p>
          )}
        </div>
      </section>

      <section className="cad-card">
        <div className="stack gap-3">
          <h2 className="cad-section-title">Retention</h2>
          <p className="cad-muted" style={{ fontSize: 'var(--cad-text-md)' }}>
            Entries older than this are removed, and the removal itself becomes a new entry —
            naming exactly what was purged, so the gap it leaves is provably a policy, not a
            cover-up. Leave blank to keep everything forever.
          </p>
          <div className="row gap-2 wrap" style={{ alignItems: 'flex-end' }}>
            <div className="cad-field">
              <label htmlFor="retention-days">Days to keep</label>
              <input
                id="retention-days"
                className="cad-input"
                type="number"
                min={1}
                style={{ width: '120px' }}
                placeholder="Forever"
                value={displayedRetention}
                onChange={(e) => {
                  setRetentionTouched(true)
                  setRetentionInput(e.target.value)
                }}
              />
            </div>
            <button
              type="button"
              className="cad-btn"
              data-variant="quiet"
              disabled={saveRetention.isPending || !retentionValid}
              onClick={() => saveRetention.mutate(retentionInput.trim() ? Number(retentionInput) : null)}
            >
              {saveRetention.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
          {!retentionValid && (
            <p className="cad-muted" style={{ fontSize: 'var(--cad-text-base)' }}>
              Enter a whole number of days, or leave it blank to keep everything forever.
            </p>
          )}
          {saveRetention.error && (
            <p className="cad-note" style={{ borderColor: 'var(--cad-bad)' }}>
              {saveRetention.error instanceof Error ? saveRetention.error.message : 'Could not save that.'}
            </p>
          )}
        </div>
      </section>

      <section className="cad-card">
        <div className="stack gap-3">
          <div className="row between wrap gap-2" style={{ alignItems: 'center' }}>
            <h2 className="cad-section-title">
              {entries.data
                ? `${entries.data.entries.length} ${entries.data.entries.length === 1 ? 'entry' : 'entries'}`
                : 'Entries'}
            </h2>
            <div className="row gap-2 wrap">
              <input
                className="cad-input"
                style={{ width: '220px' }}
                placeholder="Filter by action, e.g. team."
                value={actionFilter}
                onChange={(e) => setActionFilter(e.target.value)}
              />
              <a className="cad-btn" data-variant="quiet" href="/api/audit-log/export">
                Export CSV
              </a>
            </div>
          </div>

          {entries.isPending ? (
            <div style={{ height: '160px' }} />
          ) : entries.isError ? (
            <p className="cad-muted">
              {entries.error instanceof ApiError ? entries.error.message : 'Could not load the log.'}
            </p>
          ) : !entries.data || entries.data.entries.length === 0 ? (
            <p className="cad-muted">Nothing recorded yet.</p>
          ) : (
            <div className="cad-grid-frame">
              <table className="cad-table">
                <thead>
                  <tr>
                    <th>At</th>
                    <th>Actor</th>
                    <th>Action</th>
                    <th>Summary</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.data.entries.map((entry) => (
                    <AuditRow key={entry.id} entry={entry} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>
    </>
  )
}

function AuditRow({ entry }: { entry: AuditLogEntry }) {
  const [open, setOpen] = useState(false)
  const hasMetadata = Object.keys(entry.metadata).length > 0

  return (
    <>
      <tr
        onClick={() => hasMetadata && setOpen((v) => !v)}
        style={hasMetadata ? { cursor: 'pointer' } : undefined}
      >
        <td className="cad-muted" style={{ whiteSpace: 'nowrap' }}>
          {new Date(entry.createdAt).toLocaleString()}
        </td>
        <td>{entry.actorName ?? (entry.actorType === 'system' ? 'System' : '—')}</td>
        <td>
          <span className="cad-pill">{entry.action}</span>
        </td>
        <td>{entry.summary}</td>
      </tr>
      {open && hasMetadata && (
        <tr>
          <td colSpan={4}>
            <pre
              className="cad-muted"
              style={{ margin: 0, fontSize: 'var(--cad-text-base)', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
            >
              {JSON.stringify(entry.metadata, null, 2)}
            </pre>
          </td>
        </tr>
      )}
    </>
  )
}
